import React, { useEffect } from "react";
import Button from "components/Button";
import { certificationExportStore as certExportStore } from "../CertificationExportStore";
import { useTranslation } from "react-i18next";
import { observer } from "mobx-react";
import { buildingStore } from "store/Building/BuildingStore";
import { calculateCompliantPercentage } from "features/DynamicTable/utils";
import LoadingView from "../components/LoadingView";
import MappingStatusChart from "../components/MappingStatusChart";
import MissingEmissionsChart from "../components/EmissionsStatusChart";
import { b6ImporterStore } from "store/B6ImporterStore";

interface DatasetComplianceProps {
  compliant_objects?: number;
  total_objects?: number;
}

const LifeCycleAssessment = observer(
  ({ loading, onClose }: { loading: boolean; onClose: () => void }) => {
    const { currentBuilding } = buildingStore;
    const { t } = useTranslation();
    const [hasB6Report, setHasB6Report] = React.useState(false);

    const [datasetObjects, setDatasetObjects] =
      React.useState<DatasetComplianceProps>({
        compliant_objects: 0,
        total_objects: 0,
      });

    const percentage = calculateCompliantPercentage(
      datasetObjects.compliant_objects,
      datasetObjects.total_objects
    );

    useEffect(() => {
      certExportStore.activeStep === 1 &&
        (async () => {
          await getCompliantDataset();
          const hasReport = await b6ImporterStore.hasReportData(
            certExportStore.certification_id
          );
          setHasB6Report(Boolean(hasReport));
        })();
    }, [certExportStore.activeStep]);

    async function getCompliantDataset() {
      if (!currentBuilding) return;

      await buildingStore
        .fetchCheckDatasetCompliance(
          currentBuilding?.id,
          certExportStore.certification_id,
          certExportStore.conformity_id
        )
        .then(
          (response) =>
            response &&
            setDatasetObjects({
              compliant_objects: response.compliant_objects,
              total_objects: response.total_objects,
            })
        );
    }

    const showDescription = Boolean(datasetObjects.total_objects);

    const onContinue = () => {
      if (certExportStore.certification_key.includes("BNB")) {
        certExportStore.setActiveStep(2);
      } else if (certExportStore.certification_key.includes("18")) {
        certExportStore.setActiveStep(3);
      } else if (certExportStore.certification_key.includes("23")) {
        certExportStore.setActiveStep(4);
      } else if (certExportStore.certification_key.includes("QNG")) {
        certExportStore.setActiveStep(5);
      }
    };

    return (
      <>
        <div className="items-center justify-center flex flex-col p-6">
          <div className="text-base font-medium text-gray-800 mb-6">
            {t("certExport.datasetCheckerDesc")}
          </div>
          {(loading || !datasetObjects.total_objects) && <LoadingView />}
          <div className="flex justify-between gap-6">
            {showDescription && (
              <MappingStatusChart
                percentage={percentage}
                onClose={onClose}
                compliantCount={datasetObjects.compliant_objects}
                totalCount={datasetObjects.total_objects}
                certification={certExportStore.certification_key}
              />
            )}
            <MissingEmissionsChart
              hasReport={hasB6Report}
              onClose={onClose}
              certification={certExportStore.certification_key}
            />
          </div>
        </div>

        {!certExportStore.certification_key.includes("Level") && (
          <div className="bg-gray-50 p-4 flex justify-end">
            <Button
              width="fit-content"
              className="px-14"
              type="primary"
              onClick={onContinue}
            >
              {t("commons.next")}
            </Button>
          </div>
        )}
      </>
    );
  }
);

export default LifeCycleAssessment;
